'use client'

import { cn } from '@/utils/styles/cn'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { getFormattedCredits } from '@/features/subscription/billing/billing-plans/billing-plan/utils/billing-plan-helper'

export interface ActionBarProgressBarSegment {
    uuid: string
    totalCredits: number
    consumedCredits: number
    availableCredits: number
    color: string
    label: string
    startsAt?: string | null
    expiresAt?: string | null
}

interface ActionBarProgressBarProps {
    segments: ActionBarProgressBarSegment[]
    height?: number
    className?: string
    showTooltips?: boolean
}

export const ActionBarProgressBar = ({
    segments,
    height = 8,
    className,
    showTooltips = true,
}: ActionBarProgressBarProps) => {
    const totalCredits = segments.reduce((sum, segment) => sum + segment.totalCredits, 0)
    const totalConsumed = segments.reduce((sum, segment) => sum + segment.consumedCredits, 0)

    if (totalCredits === 0) {
        return (
            <div
                className={cn('w-full bg-[#2a2a2a]/40 rounded-full border border-orange-900/20', className)}
                style={{ height }}
            />
        )
    }

    const consumedPercentage = (totalConsumed / totalCredits) * 100
    const availableSegments = segments.filter((segment) => segment.availableCredits > 0)

    const content = (
        <div
            className={cn(
                'w-full relative rounded-full overflow-hidden bg-[#2a2a2a]/40 border border-orange-900/20',
                className
            )}
            style={{ height }}
        >
            <div className="absolute inset-0 flex">
                {availableSegments.reverse().map((segment, index) => {
                    const segmentPercentage = (segment.availableCredits / totalCredits) * 100

                    const segmentElement = (
                        <div
                            key={segment.uuid}
                            className={cn(
                                'relative cursor-pointer transition-all duration-200 hover:brightness-110 hover:z-10',
                                index > 0 && 'border-l border-[#1a1a1a]/60'
                            )}
                            style={{
                                backgroundColor: segment.color,
                                width: `${segmentPercentage}%`,
                                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                            }}
                        />
                    )

                    if (showTooltips) {
                        return (
                            <Tooltip delayDuration={50} key={segment.uuid} disableHoverableContent={true}>
                                <TooltipTrigger asChild>{segmentElement}</TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    sideOffset={12}
                                    className="bg-[#2a2a2a] text-white border-[#404040] shadow-lg pointer-events-none"
                                    avoidCollisions={true}
                                >
                                    <div className="text-center">
                                        <div className="font-medium text-xs">
                                            {getFormattedCredits(segment.availableCredits)} {segment.label}
                                        </div>
                                        {(segment.startsAt || segment.expiresAt) && (
                                            <div className="text-xs text-gray-400 mt-1">
                                                {segment.startsAt && !segment.expiresAt && (
                                                    <>
                                                        Starts:{' '}
                                                        {new Date(segment.startsAt).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}
                                                    </>
                                                )}
                                                {!segment.startsAt && segment.expiresAt && (
                                                    <>
                                                        Expires:{' '}
                                                        {new Date(segment.expiresAt).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}
                                                    </>
                                                )}
                                                {segment.startsAt && segment.expiresAt && (
                                                    <>
                                                        {new Date(segment.startsAt).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}{' '}
                                                        -{' '}
                                                        {new Date(segment.expiresAt).toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        )
                    }

                    return segmentElement
                })}

                {totalConsumed > 0 && (
                    <Tooltip delayDuration={50} disableHoverableContent={true}>
                        <TooltipTrigger asChild>
                            <div
                                className={cn(
                                    'bg-[#404040]/80 cursor-pointer transition-all duration-200 hover:bg-[#404040]',
                                    availableSegments.length > 0 && 'border-l border-[#1a1a1a]/60'
                                )}
                                style={{
                                    width: `${consumedPercentage}%`,
                                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.2)',
                                }}
                            />
                        </TooltipTrigger>
                        <TooltipContent
                            side="top"
                            sideOffset={12}
                            className="bg-[#2a2a2a] text-white border-[#404040] shadow-lg pointer-events-none"
                            avoidCollisions={true}
                        >
                            <div className="text-center">
                                <div className="font-medium text-xs">
                                    {getFormattedCredits(totalConsumed)} Used Credits
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </div>
    )

    if (showTooltips) {
        return <TooltipProvider>{content}</TooltipProvider>
    }

    return content
}
